/**
 * Pre-generated enrichment lookup served from /data/enrichment-cache.json.
 *
 * 286 kB build-time-baked descriptions/images for popular pins. Lazy-
 * loaded on first lookup (previously eager-loaded at App mount, which
 * paid the cost even for users who never opened a detail panel).
 *
 * Consumed by detail panels alongside live Wikidata/Wikipedia fetches.
 */
import type { EnrichmentData } from "../types/index.ts";

let cache: Record<string, Partial<EnrichmentData>> | null = null;
let loading: Promise<void> | null = null;

function ensureLoaded(): Promise<void> {
  if (cache) return Promise.resolve();
  if (loading) return loading;
  loading = (async () => {
    try {
      const res = await fetch("/data/enrichment-cache.json");
      if (!res.ok) { cache = {}; return; }
      cache = await res.json();
    } catch { cache = {}; }
  })();
  return loading;
}

/**
 * Synchronous accessor that kicks off the lazy fetch on first call. The
 * first detail-panel open returns null (cache still loading); subsequent
 * lookups within the same session return real data.
 *
 * Pattern picked over making this async so existing useEnrichment code
 * keeps its synchronous control flow. Trade-off: very first panel-open
 * doesn't get the pre-generated speed boost — but the user has to
 * actually open a panel for any of this to matter, so 286 kB is no
 * longer wasted on map-only visitors.
 */
export function getPregeneratedEnrichment(pinId: string): Partial<EnrichmentData> | null {
  void ensureLoaded(); // fire-and-forget; idempotent
  return cache?.[pinId] ?? null;
}

/** Legacy no-op — loading happens implicitly on first getPregeneratedEnrichment call. */
export async function loadPregeneratedEnrichment(): Promise<void> {
  // Intentionally empty.
}
